import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { LuCornerDownLeft, LuSearch } from './icons';
import { NavIcon, type IconComponent } from './navIcons';
import type { NavCategory } from './MMECNavRail';

/**
 * Command palette — the fastest route to any tool.
 *
 * ⌘K / Ctrl+K opens it from anywhere in the shell. Typing filters every tool
 * across every group; arrow keys move, Enter goes, Esc closes. With an empty
 * query it shows the tools used most recently, so the second visit to anything
 * is two keystrokes.
 *
 * Also carries a handful of shell actions (collapse rails, switch palette,
 * simple mode) so a person never has to hunt the top bar for them.
 */

export interface PaletteAction {
  id: string;
  title: string;
  /** Short hint shown at the right edge, e.g. "Layout". */
  hint?: string;
  icon?: IconComponent;
  run: () => void;
}

interface MMECCommandPaletteProps {
  open: boolean;
  onClose: () => void;
  categories: NavCategory[];
  activeId: string;
  onSelect: (id: string) => void;
  actions?: PaletteAction[];
  language?: string;
}

interface Entry {
  key: string;
  kind: 'tool' | 'action';
  id: string;
  title: string;
  group: string;
  glyph?: string;
  icon?: IconComponent;
  run: () => void;
}

const RECENT_KEY = 'mmec_palette_recent';
const RECENT_MAX = 6;

function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr.filter((x) => typeof x === 'string');
    }
  } catch {
    /* ignore */
  }
  return [];
}

function pushRecent(id: string) {
  try {
    const next = [id, ...readRecent().filter((x) => x !== id)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* ignore */
  }
}

/**
 * Rank a candidate against the query. Higher is better; 0 means no match.
 * Deliberately simple — prefix beats word-start beats substring, and the
 * group name counts for a little so "flow" surfaces the whole Flow group.
 */
function score(entry: Entry, q: string): number {
  const t = entry.title.toLowerCase();
  const g = entry.group.toLowerCase();
  const id = entry.id.toLowerCase();
  if (!q) return 1;
  if (t.startsWith(q)) return 100 - t.length * 0.1;
  if (t.split(/[\s/—-]+/).some((w) => w.startsWith(q))) return 80 - t.length * 0.1;
  if (t.includes(q)) return 60 - t.indexOf(q);
  if (id.includes(q.replace(/\s+/g, '-'))) return 50;
  if (g.startsWith(q)) return 30;
  if (g.includes(q)) return 20;
  // Loose subsequence, e.g. "rtgex" → "Real-Time GEX".
  let i = 0;
  for (const ch of t) if (ch === q[i]) i++;
  if (i === q.length) return 10 - t.length * 0.05;
  return 0;
}

const css = `
  .mmec-palette-backdrop {
    position: fixed;
    inset: 0;
    /* Above the AI companion (10050) and every tool overlay: the palette is modal. */
    z-index: 10100;
    background: color-mix(in srgb, var(--void) 55%, transparent);
    backdrop-filter: blur(6px);
    -webkit-backdrop-filter: blur(6px);
    display: flex;
    align-items: flex-start;
    justify-content: center;
    padding: 12vh 16px 16px;
    animation: mmec-fade-in var(--dur-control) var(--ease) both;
  }
  .mmec-palette {
    width: min(620px, 100%);
    max-height: min(560px, 76vh);
    display: flex;
    flex-direction: column;
    background: var(--elev);
    border: 1px solid var(--border);
    border-radius: 14px;
    box-shadow: var(--lift-3);
    overflow: hidden;
    font-family: var(--font-ui);
    animation: mmec-scale-in var(--dur-panel) var(--ease) both;
  }
  .mmec-palette-input-row {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 0 16px;
    height: 52px;
    border-bottom: 1px solid var(--hair);
    color: var(--muted);
  }
  .mmec-palette-input {
    flex: 1;
    min-width: 0;
    height: 100%;
    border: none;
    background: transparent;
    color: var(--txt);
    font: 400 15px/1 var(--font-ui);
    outline: none;
  }
  .mmec-palette-input::placeholder { color: var(--muted-2); }
  .mmec-palette-input:focus-visible { outline: none; }
  .mmec-palette-list {
    flex: 1;
    min-height: 0;
    overflow-y: auto;
    padding: 6px;
  }
  .mmec-palette-section {
    padding: 10px 10px 4px;
  }
  .mmec-palette-row {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    height: 40px;
    padding: 0 10px;
    border: none;
    border-radius: 8px;
    background: transparent;
    color: var(--txt-2);
    font: 400 13px/1 var(--font-ui);
    text-align: start;
    cursor: pointer;
    transition: background var(--dur-micro) var(--ease), color var(--dur-micro) var(--ease);
  }
  .mmec-palette-row.is-selected {
    background: var(--acc-dim);
    color: var(--txt);
  }
  .mmec-palette-row:focus-visible { outline: none; }
  .mmec-palette-row-icon {
    width: 18px;
    height: 18px;
    display: grid;
    place-items: center;
    color: var(--muted);
    flex-shrink: 0;
  }
  .mmec-palette-row.is-selected .mmec-palette-row-icon { color: var(--acc); }
  .mmec-palette-row-title {
    flex: 1;
    min-width: 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  .mmec-palette-row-group {
    flex-shrink: 0;
    font: 500 10.5px/1 var(--font-ui);
    letter-spacing: 0.04em;
    color: var(--muted-2);
  }
  .mmec-palette-row-enter {
    flex-shrink: 0;
    color: var(--acc);
    opacity: 0;
    transition: opacity var(--dur-micro) var(--ease);
  }
  .mmec-palette-row.is-selected .mmec-palette-row-enter { opacity: 1; }
  .mmec-palette-current {
    font: 500 9.5px/1 var(--font-mono);
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: var(--rail-act);
    border: 1px solid color-mix(in srgb, var(--rail-act) 40%, transparent);
    border-radius: 4px;
    padding: 2px 4px;
    flex-shrink: 0;
  }
  .mmec-palette-empty {
    padding: 28px 16px;
    text-align: center;
    color: var(--muted);
    font-size: 13px;
  }
  .mmec-palette-empty strong { color: var(--txt); font-weight: 500; }
  .mmec-palette-footer {
    display: flex;
    align-items: center;
    gap: 14px;
    height: 36px;
    padding: 0 16px;
    border-top: 1px solid var(--hair);
    color: var(--muted-2);
    font: 500 10.5px/1 var(--font-ui);
  }
  .mmec-palette-footer span { display: inline-flex; align-items: center; gap: 5px; }
  .mmec-palette-footer .mmec-kbd { height: 16px; min-width: 16px; font-size: 9.5px; }
`;

export const MMECCommandPalette: React.FC<MMECCommandPaletteProps> = ({
  open,
  onClose,
  categories,
  activeId,
  onSelect,
  actions = [],
  language = 'en',
}) => {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const isAr = language === 'ar';

  const entries = useMemo<Entry[]>(() => {
    const tools: Entry[] = categories.flatMap((c) =>
      c.items.map((i) => ({
        key: `tool:${i.id}`,
        kind: 'tool' as const,
        id: i.id,
        title: i.title,
        group: c.title,
        glyph: i.icon,
        run: () => onSelect(i.id),
      })),
    );
    const acts: Entry[] = actions.map((a) => ({
      key: `action:${a.id}`,
      kind: 'action' as const,
      id: a.id,
      title: a.title,
      group: a.hint ?? (isAr ? 'إجراء' : 'Action'),
      icon: a.icon,
      run: a.run,
    }));
    return [...tools, ...acts];
  }, [categories, actions, onSelect, isAr]);

  const q = query.trim().toLowerCase();

  const sections = useMemo<{ title: string; items: Entry[] }[]>(() => {
    if (q) {
      const ranked = entries
        .map((e) => ({ e, s: score(e, q) }))
        .filter((x) => x.s > 0)
        .sort((a, b) => b.s - a.s)
        .map((x) => x.e);
      return ranked.length ? [{ title: isAr ? 'النتائج' : 'Results', items: ranked }] : [];
    }
    const recentIds = readRecent();
    const byId = new Map(entries.filter((e) => e.kind === 'tool').map((e) => [e.id, e]));
    const recent = recentIds.map((id) => byId.get(id)).filter((e): e is Entry => Boolean(e) && e!.id !== activeId);
    const out: { title: string; items: Entry[] }[] = [];
    if (recent.length) out.push({ title: isAr ? 'الأخيرة' : 'Recent', items: recent });
    for (const c of categories) {
      out.push({ title: c.title, items: entries.filter((e) => e.kind === 'tool' && e.group === c.title) });
    }
    if (actions.length) out.push({ title: isAr ? 'إجراءات' : 'Actions', items: entries.filter((e) => e.kind === 'action') });
    return out;
  }, [q, entries, categories, actions.length, activeId, isAr]);

  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  // Reset on open; keep the selection in range as results change.
  useEffect(() => {
    if (open) {
      setQuery('');
      setSelected(0);
      // Focus after the entrance frame so the caret does not jump mid-scale.
      const t = window.setTimeout(() => inputRef.current?.focus(), 30);
      return () => window.clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => {
    setSelected(0);
  }, [q]);

  useEffect(() => {
    const el = listRef.current?.querySelector<HTMLElement>(`[data-idx="${selected}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [selected]);

  const commit = useCallback(
    (entry: Entry | undefined) => {
      if (!entry) return;
      if (entry.kind === 'tool') pushRecent(entry.id);
      onClose();
      // Let the overlay leave first so the destination view's entrance is not
      // fighting the palette's exit for the same frame.
      window.setTimeout(entry.run, 0);
    },
    [onClose],
  );

  const onKeyDown = (e: React.KeyboardEvent) => {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelected((s) => (flat.length ? (s + 1) % flat.length : 0));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelected((s) => (flat.length ? (s - 1 + flat.length) % flat.length : 0));
        break;
      case 'Home':
        e.preventDefault();
        setSelected(0);
        break;
      case 'End':
        e.preventDefault();
        setSelected(Math.max(0, flat.length - 1));
        break;
      case 'Enter':
        e.preventDefault();
        commit(flat[selected]);
        break;
      case 'Escape':
        e.preventDefault();
        onClose();
        break;
      default:
        break;
    }
  };

  if (!open) return null;

  let runningIdx = -1;

  return createPortal(
    <>
      <style>{css}</style>
      <div
        className="mmec-palette-backdrop"
        onMouseDown={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
      >
        <div
          className="mmec-palette"
          role="dialog"
          aria-modal="true"
          aria-label={isAr ? 'الانتقال إلى أداة' : 'Go to tool'}
          onKeyDown={onKeyDown}
          dir={isAr ? 'rtl' : 'ltr'}
        >
          <div className="mmec-palette-input-row">
            <LuSearch size={17} strokeWidth={2} aria-hidden />
            <input
              ref={inputRef}
              className="mmec-palette-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={isAr ? 'اكتب اسم أداة…' : 'Type a tool name…'}
              aria-label={isAr ? 'ابحث' : 'Search'}
              autoComplete="off"
              autoCorrect="off"
              spellCheck={false}
              role="combobox"
              aria-expanded
              aria-controls="mmec-palette-list"
              aria-activedescendant={flat[selected] ? `mmec-palette-opt-${selected}` : undefined}
            />
            <span className="mmec-kbd" aria-hidden>esc</span>
          </div>

          <div className="mmec-palette-list" id="mmec-palette-list" role="listbox" ref={listRef}>
            {sections.length === 0 && (
              <div className="mmec-palette-empty">
                {isAr ? 'لا توجد أداة تطابق' : 'No tool matches'} <strong>{query}</strong>
              </div>
            )}
            {sections.map((section) => (
              <div key={section.title}>
                <div className="mmec-palette-section mmec-eyebrow">{section.title}</div>
                {section.items.map((entry) => {
                  runningIdx += 1;
                  const idx = runningIdx;
                  const isSel = idx === selected;
                  const isCurrent = entry.kind === 'tool' && entry.id === activeId;
                  const ActionIcon = entry.icon;
                  return (
                    <button
                      key={entry.key}
                      type="button"
                      id={`mmec-palette-opt-${idx}`}
                      data-idx={idx}
                      role="option"
                      aria-selected={isSel}
                      className={`mmec-palette-row${isSel ? ' is-selected' : ''}`}
                      onMouseEnter={() => setSelected(idx)}
                      onClick={() => commit(entry)}
                      tabIndex={-1}
                    >
                      <span className="mmec-palette-row-icon">
                        {entry.kind === 'tool' ? (
                          <NavIcon id={entry.id} fallback={entry.glyph} size={17} />
                        ) : ActionIcon ? (
                          <ActionIcon size={17} strokeWidth={1.75} aria-hidden />
                        ) : null}
                      </span>
                      <span className="mmec-palette-row-title">{entry.title}</span>
                      {isCurrent && <span className="mmec-palette-current">{isAr ? 'الحالية' : 'current'}</span>}
                      <span className="mmec-palette-row-group">{entry.group}</span>
                      <LuCornerDownLeft size={14} className="mmec-palette-row-enter" aria-hidden />
                    </button>
                  );
                })}
              </div>
            ))}
          </div>

          <div className="mmec-palette-footer">
            <span><span className="mmec-kbd">↑</span><span className="mmec-kbd">↓</span> {isAr ? 'تنقل' : 'move'}</span>
            <span><span className="mmec-kbd">↵</span> {isAr ? 'فتح' : 'open'}</span>
            <span><span className="mmec-kbd">esc</span> {isAr ? 'إغلاق' : 'close'}</span>
          </div>
        </div>
      </div>
    </>,
    document.body,
  );
};

export default MMECCommandPalette;
